import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { authService } from '../services/auth.service';
import { tokenUtils, adminTokenUtils } from '../axios.config';
import { QUERY_KEYS } from '../constants';
import type { ApiResponse } from '../types/common';
import type {
  UserProfile,
  AdminLoginRequest,
  AdminLoginResponse,
} from '../types/auth.types';

/**
 * Hook to get authenticated user profile
 */
export const useUserProfile = (
  enabled?: boolean
): UseQueryResult<ApiResponse<UserProfile>, Error> => {
  const hasToken = !!tokenUtils.getToken();

  return useQuery({
    queryKey: QUERY_KEYS.AUTH.USER_PROFILE,
    queryFn: () => authService.getUserProfile(),
    enabled: enabled !== undefined ? enabled : hasToken,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to perform admin login - stores token in separate admin storage
 */
export const useAdminLogin = (): UseMutationResult<
  ApiResponse<AdminLoginResponse>,
  Error,
  AdminLoginRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AdminLoginRequest) => authService.adminLogin(payload),
    onSuccess: (data) => {
      adminTokenUtils.setToken(data.data.token);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.ADMIN_SESSION });
    },
    onError: () => {
      adminTokenUtils.removeToken();
    },
  });
};

/**
 * Hook to verify admin session
 */
export const useAdminSession = (enabled = true): UseQueryResult<
  ApiResponse<{ role: string; adminId: string }>,
  Error
> => {
  const hasAdminToken = adminTokenUtils.hasToken();

  return useQuery({
    queryKey: QUERY_KEYS.AUTH.ADMIN_SESSION,
    queryFn: () => authService.getAdminSession(),
    enabled: enabled && hasAdminToken,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to logout user (clears token and invalidates queries)
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  const logout = () => {
    tokenUtils.removeToken();
    queryClient.clear();
  };

  return { logout };
};

/**
 * Hook to logout admin (clears admin token)
 */
export const useAdminLogout = () => {
  const queryClient = useQueryClient();

  const logout = () => {
    adminTokenUtils.removeToken();
    queryClient.removeQueries({ queryKey: QUERY_KEYS.AUTH.ADMIN_SESSION });
  };

  return { logout };
};
